import { useCallback, useEffect, useRef, useState } from 'react'
import { BROWSER_DEFAULTS, deviceChoices } from './devices'

/** Why the check has no picture or sound to show. */
export type CameraCheckProblem = 'insecure' | 'unsupported' | 'denied' | 'missing' | 'mic_missing' | 'busy' | 'failed'

/** A camera or microphone the browser lists. */
export interface CameraCheckDevice {
  deviceId: string
  label: string
}

/**
 * Where the check is. `opening`: asking the browser (the first time it may ask the person for permission). `ready`: a
 * local stream with a microphone, and a camera unless `hasCamera` is false. `failed`: see `problem`. `released`: the
 * devices were handed back so the page can open its own (joining). `idle`: not open.
 */
export type CameraCheckPhase = 'idle' | 'opening' | 'ready' | 'failed' | 'released'

export interface CameraCheck {
  phase: CameraCheckPhase
  /** The first opening since the check opened: the browser may be asking for permission. */
  asking: boolean
  problem: CameraCheckProblem | null
  /** The local preview's stream while ready. Never played back with sound. */
  stream: MediaStream | null
  /** False when there is no camera and the check opened the microphone alone. */
  hasCamera: boolean
  cameraOn: boolean
  micOn: boolean
  /** The front camera, or one whose facing the browser doesn't say: mirror it like a face. */
  facingUser: boolean
  cameras: CameraCheckDevice[]
  microphones: CameraCheckDevice[]
  videoDeviceId: string | null
  audioDeviceId: string | null
  /** Opens again (Try again, or after a join that didn't happen), with the devices picked. */
  retry(): void
  /** Stops every track, so the page can open the devices itself. `retry` opens them again. */
  release(): void
  toggleCamera(): void
  toggleMic(): void
  selectCamera(deviceId: string): void
  selectMicrophone(deviceId: string): void
}

interface Snapshot {
  phase: CameraCheckPhase
  asking: boolean
  problem: CameraCheckProblem | null
  stream: MediaStream | null
  hasCamera: boolean
  facingUser: boolean
  cameras: CameraCheckDevice[]
  microphones: CameraCheckDevice[]
  videoDeviceId: string | null
  audioDeviceId: string | null
}

const IDLE: Snapshot = {
  phase: 'idle',
  asking: false,
  problem: null,
  stream: null,
  hasCamera: false,
  facingUser: true,
  cameras: [],
  microphones: [],
  videoDeviceId: null,
  audioDeviceId: null,
}

/** The devices to open: the ones picked, or the browser's choice (the front camera) without. */
interface Wanted {
  video: string | null
  audio: string | null
}

/**
 * The camera and microphone check before going on stage: a local `getUserMedia` stream for the preview, the browser's
 * cameras and microphones, mute toggles (the tracks' `enabled`) and what went wrong. Opens while `active`; every track
 * stops when it turns inactive or the component unmounts, and a stream the browser gives after that is stopped at once.
 * Without a camera it opens the microphone alone, so the person can join with sound only.
 */
export function useCameraCheck(active: boolean): CameraCheck {
  const [snapshot, setSnapshot] = useState<Snapshot>(IDLE)
  const [cameraOn, setCameraOn] = useState(true)
  const [micOn, setMicOn] = useState(true)
  const streamRef = useRef<MediaStream | null>(null)
  /** Bumped by every opening and every stop, so an answer from an older one is dropped (and its stream stopped). */
  const runRef = useRef(0)
  const wantedRef = useRef<Wanted>({ video: null, audio: null })
  const cameraOnRef = useRef(true)
  const micOnRef = useRef(true)

  const stop = useCallback(() => {
    runRef.current += 1
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }, [])

  const listDevices = useCallback(async (run: number) => {
    const devices = await enumerate()
    if (run !== runRef.current) return
    setSnapshot((current) => ({
      ...current,
      cameras: deviceChoices(devices, 'videoinput'),
      microphones: deviceChoices(devices, 'audioinput'),
      // A track on the browser's "default" entry names the real device the pickers list instead.
      videoDeviceId: listedId(current.videoDeviceId, devices),
      audioDeviceId: listedId(current.audioDeviceId, devices),
    }))
  }, [])

  const open = useCallback(async (asking: boolean) => {
    stop()
    const run = runRef.current
    const wanted = wantedRef.current
    setSnapshot((current) => ({ ...current, phase: 'opening', asking, problem: null, stream: null }))
    const fail = (problem: CameraCheckProblem) => {
      if (run === runRef.current) setSnapshot((current) => ({ ...current, phase: 'failed', problem, stream: null }))
    }

    const unavailable = mediaUnavailable()
    if (unavailable) return fail(unavailable)
    const media = navigator.mediaDevices
    const audio: MediaTrackConstraints | boolean = wanted.audio ? { deviceId: { exact: wanted.audio } } : true

    let stream: MediaStream
    try {
      stream = await media.getUserMedia({ video: wanted.video ? { deviceId: { exact: wanted.video } } : { facingMode: 'user' }, audio })
    } catch (failure) {
      if (run !== runRef.current) return
      const problem = problemOf(failure)
      if (problem !== 'missing') return fail(problem)
      // No camera (or not the one picked): the microphone alone, so the person can still join with sound.
      try {
        stream = await media.getUserMedia({ audio })
      } catch (second) {
        if (run !== runRef.current) return
        const next = problemOf(second)
        return fail(next === 'missing' ? await whichMissing() : next)
      }
    }
    // Closed, or opened again, while the browser asked: this stream is no one's.
    if (run !== runRef.current) {
      stream.getTracks().forEach((track) => track.stop())
      return
    }

    streamRef.current = stream
    const video = stream.getVideoTracks()[0]
    const microphone = stream.getAudioTracks()[0]
    if (video) video.enabled = cameraOnRef.current
    if (microphone) microphone.enabled = micOnRef.current
    const videoSettings = video?.getSettings?.() ?? {}
    setSnapshot((current) => ({
      ...current,
      phase: 'ready',
      problem: null,
      stream,
      hasCamera: Boolean(video),
      facingUser: videoSettings.facingMode !== 'environment',
      videoDeviceId: video ? videoSettings.deviceId ?? wanted.video : null,
      audioDeviceId: microphone?.getSettings?.().deviceId ?? wanted.audio,
    }))
    void listDevices(run)
  }, [listDevices, stop])

  useEffect(() => {
    if (!active) return
    wantedRef.current = { video: null, audio: null }
    cameraOnRef.current = true
    micOnRef.current = true
    setCameraOn(true)
    setMicOn(true)
    void open(true)
    const media = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined
    // A camera plugged in or out while the check is open joins or leaves the pickers.
    const onChange = () => void listDevices(runRef.current)
    media?.addEventListener?.('devicechange', onChange)
    return () => {
      media?.removeEventListener?.('devicechange', onChange)
      stop()
      setSnapshot(IDLE)
    }
  }, [active, open, listDevices, stop])

  const retry = useCallback(() => void open(false), [open])

  const release = useCallback(() => {
    stop()
    setSnapshot((current) => ({ ...current, phase: 'released', stream: null }))
  }, [stop])

  const toggleCamera = useCallback(() => {
    cameraOnRef.current = !cameraOnRef.current
    streamRef.current?.getVideoTracks().forEach((track) => (track.enabled = cameraOnRef.current))
    setCameraOn(cameraOnRef.current)
  }, [])

  const toggleMic = useCallback(() => {
    micOnRef.current = !micOnRef.current
    streamRef.current?.getAudioTracks().forEach((track) => (track.enabled = micOnRef.current))
    setMicOn(micOnRef.current)
  }, [])

  const selectCamera = useCallback((deviceId: string) => {
    wantedRef.current = { video: deviceId, audio: wantedRef.current.audio ?? snapshotAudio(streamRef.current) }
    void open(false)
  }, [open])

  const selectMicrophone = useCallback((deviceId: string) => {
    wantedRef.current = { video: wantedRef.current.video ?? snapshotVideo(streamRef.current), audio: deviceId }
    void open(false)
  }, [open])

  return { ...snapshot, cameraOn, micOn, retry, release, toggleCamera, toggleMic, selectCamera, selectMicrophone }
}

/** The device the open stream's camera or microphone uses, so switching the other keeps it. */
const snapshotVideo = (stream: MediaStream | null) => stream?.getVideoTracks()[0]?.getSettings?.().deviceId ?? null
const snapshotAudio = (stream: MediaStream | null) => stream?.getAudioTracks()[0]?.getSettings?.().deviceId ?? null

/**
 * Why the browser has no camera to give before asking: off a secure origin (an http address other than localhost) it
 * hides `mediaDevices`; a few browsers have no getUserMedia at all.
 */
function mediaUnavailable(): 'insecure' | 'unsupported' | null {
  if (typeof window !== 'undefined' && window.isSecureContext === false) return 'insecure'
  if (typeof navigator === 'undefined' || typeof navigator.mediaDevices?.getUserMedia !== 'function') return 'unsupported'
  return null
}

/** A getUserMedia refusal, by the DOMException's name. */
function problemOf(failure: unknown): CameraCheckProblem {
  switch ((failure as { name?: unknown } | null)?.name) {
    case 'NotAllowedError':
    case 'SecurityError':
    case 'PermissionDeniedError':
      return 'denied'
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'missing'
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'busy'
    default:
      return 'failed'
  }
}

async function enumerate(): Promise<MediaDeviceInfo[]> {
  try {
    return (await navigator.mediaDevices?.enumerateDevices?.()) ?? []
  } catch {
    return []
  }
}

/** With no microphone either: a camera alone can't go on stage, so which is missing says what to connect. */
async function whichMissing(): Promise<CameraCheckProblem> {
  const devices = await enumerate()
  const has = (kind: MediaDeviceKind) => devices.some((device) => device.kind === kind)
  return has('videoinput') && !has('audioinput') ? 'mic_missing' : 'missing'
}


/** The device the pickers list for `deviceId`: itself, or for a browser default, the real device in its group. */
function listedId(deviceId: string | null, devices: MediaDeviceInfo[]): string | null {
  if (!deviceId || !BROWSER_DEFAULTS.has(deviceId)) return deviceId
  const entry = devices.find((device) => device.deviceId === deviceId)
  const real = entry && devices.find((device) => device.kind === entry.kind && device.groupId === entry.groupId && !BROWSER_DEFAULTS.has(device.deviceId))
  return real?.deviceId ?? deviceId
}

/** How many bars the level meter has. */
export const MIC_LEVEL_BARS = 6
/** The quietest level that lights a bar, and the level that lights them all, in dBFS. */
const FLOOR_DB = -60
const CEILING_DB = -10
/** With reduced motion the meter updates in steps this far apart, rather than every frame. */
export const REDUCED_MOTION_LEVEL_MS = 500

/** Bars lit for a root mean square level (0–1), on a decibel scale from the floor to the ceiling. */
export function micLevelBars(rms: number, bars = MIC_LEVEL_BARS): number {
  if (!(rms > 0)) return 0
  const share = (20 * Math.log10(rms) - FLOOR_DB) / (CEILING_DB - FLOOR_DB)
  return Math.max(0, Math.min(bars, Math.round(share * bars)))
}

/**
 * The microphone's level now, in bars lit (0 to `MIC_LEVEL_BARS`), read from the stream with the Web Audio API: every
 * animation frame, or every `REDUCED_MOTION_LEVEL_MS` with `reduced`. 0 while muted, without a microphone, or where the
 * browser has no Web Audio. Re-renders only when the count changes.
 */
export function useMicLevel(stream: MediaStream | null, on: boolean, reduced: boolean): number {
  const [level, setLevel] = useState(0)
  useEffect(() => {
    setLevel(0)
    if (!stream || !on || stream.getAudioTracks().length === 0) return
    const AudioContextClass =
      typeof window === 'undefined'
        ? undefined
        : window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioContextClass) return
    let context: AudioContext
    let source: MediaStreamAudioSourceNode
    let analyser: AnalyserNode
    try {
      context = new AudioContextClass()
      analyser = context.createAnalyser()
      analyser.fftSize = 1024
      source = context.createMediaStreamSource(stream)
      source.connect(analyser)
    } catch {
      return
    }
    // Opened from the check's own press, so the browser lets it run; resume in case it started suspended.
    void Promise.resolve(context.resume?.()).catch(() => {})
    const samples = new Uint8Array(analyser.fftSize)
    let shown = 0
    const read = () => {
      analyser.getByteTimeDomainData(samples)
      let sum = 0
      for (const sample of samples) {
        const centred = (sample - 128) / 128
        sum += centred * centred
      }
      const next = micLevelBars(Math.sqrt(sum / samples.length))
      if (next !== shown) {
        shown = next
        setLevel(next)
      }
    }
    let frame = 0
    let timer: number | undefined
    if (reduced) {
      timer = window.setInterval(read, REDUCED_MOTION_LEVEL_MS)
    } else {
      const loop = () => {
        read()
        frame = window.requestAnimationFrame(loop)
      }
      frame = window.requestAnimationFrame(loop)
    }
    return () => {
      window.cancelAnimationFrame(frame)
      window.clearInterval(timer)
      source.disconnect()
      void Promise.resolve(context.close?.()).catch(() => {})
    }
  }, [stream, on, reduced])
  return on ? level : 0
}
